import { getState } from '../store/identity-store.js';
import {
  ensureFreshPlatformJwt,
  isPlatformJwtFresh,
  renewAfterRejection,
} from './sso.js';
import type { ApiRequestAuthority } from './api-request-authority.js';

/**
 * Browsers allow 64 KiB of keepalive request bodies in flight per page, counted across every such
 * request at once. A request that would go over this budget goes out as an ordinary one instead of
 * being refused outright.
 */
const KEEPALIVE_BODY_LIMIT_BYTES = 60_000;

/** Bytes of keepalive bodies sent and not settled yet, on this page. */
let keepaliveBytesInFlight = 0;

/**
 * The authenticated `fetch` for every `/v1` call.
 *
 * Two jobs, both of which every API module used to hand-roll (badly): take the
 * bearer from the renewal core rather than from the store, so a token near
 * expiry is renewed *before* the request goes out; and survive the 401 that
 * happens anyway when a token dies between being attached and being read by
 * the server — a real window now that the platform JWT lives 15 minutes.
 *
 * Callers keep their own URLs, methods, bodies and headers. This only sets
 * `Authorization`, so a module adopts it by swapping `fetch` for `apiFetch`
 * and dropping the token line from its own header builder.
 * Acknowledged actions can disable the 401 replay without disabling proactive renewal.
 */
export async function apiFetch(
  input: string,
  init: RequestInit = {},
  options: { retryUnauthorized?: boolean } = {},
  authority?: ApiRequestAuthority,
): Promise<Response> {
  authority?.assertCurrent();
  const token = await ensureFreshPlatformJwt();
  authority?.assertCurrent(token);
  const response = await fetch(input, withBearer(init, token));
  if (response.status !== 401 || options.retryUnauthorized === false)
    return response;
  // The retry asks for a renewal that ignores `exp` and the failure cooldown:
  // the server has rejected this token, so its own expiry claim is irrelevant,
  // and the proactive attempt that may have just failed must not eat the one
  // renewal this 401 justifies. Without that, the recovery path was unreachable
  // in exactly the cases it exists for.
  authority?.assertCurrent();
  const renewed = await renewAfterRejection(token);
  authority?.assertCurrent();
  if (renewed === null || renewed.length === 0 || renewed === token)
    return response;
  authority?.assertCurrent(renewed);
  return fetch(input, withBearer(init, renewed));
}

/**
 * A request that must outlive the page: a save sent while the tab is being hidden, suspended or torn
 * down. It goes out synchronously with `keepalive` and the bearer already in the store, because a page
 * on its way out cannot wait for a renewal round-trip. A body that would take the keepalive requests
 * in flight over their shared budget falls back to {@link apiFetch}, which may still finish if the page
 * lives long enough. A save whose stored bearer has expired renews first and then still goes out with
 * `keepalive` — after a quiet quarter of an hour that is the usual case, and sending the dead token
 * would only buy a certain 401.
 */
export function keepaliveFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const stored = getState().platformJwt;
  if (stored !== null && !isPlatformJwtFresh(stored, undefined, 0)) {
    return ensureFreshPlatformJwt().then((renewed) =>
      sendKeepalive(input, init, renewed ?? stored),
    );
  }
  return sendKeepalive(input, init, stored);
}

/**
 * Sends one keepalive request with `token`, counting its body against the shared budget until it
 * settles. A body over the budget goes out through {@link apiFetch} instead.
 */
function sendKeepalive(
  input: string,
  init: RequestInit,
  token: string | null,
): Promise<Response> {
  const bodyBytes = requestBodyBytes(init.body);
  if (keepaliveBytesInFlight + bodyBytes > KEEPALIVE_BODY_LIMIT_BYTES) {
    return apiFetch(input, init);
  }
  keepaliveBytesInFlight += bodyBytes;
  return fetch(input, {
    ...withBearer(init, token),
    keepalive: true,
  }).finally(() => {
    keepaliveBytesInFlight -= bodyBytes;
  });
}

/** Size of a string body in bytes; other body kinds are not used by keepalive callers. */
function requestBodyBytes(body: RequestInit['body']): number {
  return typeof body === 'string' ? new TextEncoder().encode(body).length : 0;
}

/**
 * Copies the caller's init with the bearer set. A `Headers` instance is used so
 * a caller's own `Authorization` — and any header shape (`Record`, tuples,
 * `Headers`) — is normalised instead of silently duplicated.
 */
function withBearer(init: RequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers);
  // The header is owned outright, not merged: when the renewal core says there is
  // no usable session — signed out, or refused by the access gate — the request
  // has to carry no bearer at all, rather than whatever a caller happened to put
  // there. Anything else would let a stale token outlive the session it came from.
  headers.delete('Authorization');
  if (token !== null && token.length > 0) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}
